import { useState } from 'react';
import { darkTheme } from '../../../lib/tokens';
import { formatDate } from '../formatDate';
import type { PipelineDoc } from '../data/types';
import { FactsStrip } from './FactsStrip';
import { StageFlow } from './StageFlow';
import { StageCard } from './StageCard';
import { GateTable } from './GateTable';
import { ArtifactList, GapList, LessonList, RefreshList, Section, SourceList } from './SectionBlocks';

interface PipelineBodyProps {
  doc: PipelineDoc;
  /** Praesentationsmodus: groessere Schrift, keine Pflege-Angaben. */
  large?: boolean;
}

/**
 * Der gemeinsame Koerper einer Pipeline-Seite. Detailansicht und
 * Praesentationsmodus rendern denselben Inhalt — sonst weicht das, was im
 * Termin gezeigt wird, von dem ab, was gepflegt wird.
 */
export function PipelineBody({ doc, large = false }: PipelineBodyProps): JSX.Element {
  const [activeStage, setActiveStage] = useState<string | undefined>(undefined);

  function jumpTo(id: string): void {
    setActiveStage(id);
    document.getElementById(`stufe-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <>
      <p className={`max-w-4xl font-semibold text-text-primary ${large ? 'text-2xl leading-snug' : 'text-base'}`}>
        {doc.claim}
      </p>

      <div className={`mt-4 max-w-4xl space-y-3 ${large ? 'text-lg leading-relaxed' : 'text-sm leading-relaxed'}`}>
        {doc.summary.map((p) => (
          <p key={p.slice(0, 40)} className="text-text-secondary">
            {p}
          </p>
        ))}
      </div>

      <Section title="Kennzahlen" lead={`Stand ${formatDate(doc.asOf)} — jede Zahl mit ihrer Herkunft.`} large={large}>
        <FactsStrip facts={doc.facts} large={large} />
      </Section>

      <Section title="Die Strecke" large={large}>
        <StageFlow stages={doc.stages} activeId={activeStage} onSelect={jumpTo} />
        <div className="mt-4 space-y-4">
          {doc.stages.map((s) => (
            <StageCard key={s.id} stage={s} large={large} />
          ))}
        </div>
      </Section>

      <Section
        title="Wächter"
        lead="Was automatisch geprüft wird — und wofür jede Prüfung blind ist."
        large={large}
      >
        <GateTable gates={doc.gates} large={large} />
        <p className={`mt-3 text-text-tertiary ${large ? 'text-base' : 'text-xs'}`}>
          <strong style={{ color: darkTheme.colors.textSecondary }}>Gegen Null</strong> heißt: jeder Verstoß
          blockt. <strong style={{ color: darkTheme.colors.textSecondary }}>Ratchet</strong> heißt: gegen eine
          Grundlinie mit namentlich geführten Fällen — Neues blockt, Bekanntes nicht. Das ist kein
          Kompromiss, sondern die Bedingung dafür, dass ein Wächter über Monate eingeschaltet bleibt.
        </p>
      </Section>

      <Section title="Artefakte" lead="Was die Strecke hinterlässt und wo es liegt." large={large}>
        <ArtifactList artifacts={doc.artifacts} large={large} />
      </Section>

      <Section title="Kosten und Durchsatz" large={large}>
        <FactsStrip facts={doc.costs} large={large} />
      </Section>

      <Section title="Fehler und die Regeln daraus" large={large}>
        <LessonList lessons={doc.lessons} large={large} />
      </Section>

      <Section
        title="Bekannter Rückstand"
        lead="Bewusst sichtbar — ein Bild ohne offene Punkte wäre kein ehrliches Bild."
        large={large}
      >
        <GapList gaps={doc.gaps} large={large} />
      </Section>

      {!large && (
        <>
          <Section title="Belege im Repo">
            <SourceList sources={doc.sources} />
          </Section>
          <Section title="Zahlen neu erheben" lead="Wenn der Stand altert, führen diese Befehle ihn nach.">
            <RefreshList items={doc.refresh} />
          </Section>
        </>
      )}
    </>
  );
}
